import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logger from '../utils/logger';
import CoverPageSection from './SampleESAReport/CoverPage';
import ExecutiveSummarySection from './SampleESAReport/ExecutiveSummary';
import PriorityOpportunitiesSection from './SampleESAReport/PriorityOpportunities';
import FullOpportunitiesListSection from './SampleESAReport/FullOpportunitiesList';
import AssumptionsLimitationsSection from './SampleESAReport/AssumptionsLimitations';
import MethodologyDataSourcesSection from './SampleESAReport/MethodologyDataSources';
import NextStepsSection from './SampleESAReport/NextSteps';
import ProductivityRoadmapSection from './SampleESAReport/ProductivityRoadmap';
import BenchmarkComparisonSection from './SampleESAReport/BenchmarkComparison';
import ProductivityFactorsSection from './SampleESAReport/ProductivityFactors';

const SampleESAReport = () => {
  const [activeTab, setActiveTab] = useState('cover');
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load real analysis data using ChatGPT specification
  useEffect(() => {
    const loadRealAnalysis = async () => {
      try {
        setLoading(true);
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        
        const response = await fetch(`${backendUrl}/api/analysis/test`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          logger.log('Professional Analysis Engine Response:', data);
          
          if (data.status === 'success' && data.test_results) {
            setAnalysisData(data.test_results);
          } else {
            logger.warn('Analysis engine test failed:', data);
            setAnalysisData(null);
          }
        } else {
          logger.warn('Analysis engine not available, using sample data');
          setAnalysisData(null);
        }
      } catch (error) {
        logger.warn('Error loading real analysis, using sample data:', error);
        setAnalysisData(null);
      } finally {
        setLoading(false);
      }
    };

    loadRealAnalysis();
  }, []);

  // Transform analysis data into display format
  const getDisplayData = () => {
    if (analysisData && analysisData.summary && analysisData.category_breakdown) {
      const summary = analysisData.summary;
      const categories = analysisData.category_breakdown || [];
      const benchmark = analysisData.benchmark || {};
      const opportunities = analysisData.opportunities || [];
      
      return {
        annualSavings: summary.estimated_annual_savings_aud || 0,
        measuresCount: summary.num_opportunities_identified || 0,
        energySaved: summary.estimated_annual_kwh_saved || 0,
        paybackPeriod: summary.median_payback_years || 0,
        roi: summary.roi_pct || 0,
        currentPercentile: benchmark.facility_percentile || 65,
        improvedPercentile: benchmark.projected_percentile_after_measures || 78,
        realCategories: categories.map(cat => ({
          name: cat.category,
          count: cat.count,
          savings: cat.savings_aud,
          energySaved: cat.savings_kwh
        })),
        isRealAnalysis: true,
        methodology: "Equipment-based analysis using DOE+RASA models",
        engineVersion: analysisData.meta?.engine_version || "rasa-1.0.0"
      };
    } else {
      return sampleDataFallback;
    }
  };

  // Enhanced sample data (kept as fallback)
  const sampleDataFallback = {
    facility: "Wholesale Baking Co.",
    location: "Melbourne Production Facility, Victoria", 
    date: "September 25, 2025",
    annualSavings: 128450,
    measuresCount: 14,
    energySaved: 934000,
    peakDemand: 210,
    paybackPeriod: 1.6,
    roi: 62.5,
    facilityScore: 85,
    industryAverage: 70,
    topQuartile: 95,
    currentPercentile: 65,
    improvedPercentile: 78,
    
    productivityRoadmap: {
      quickWins: {
        timeframe: "0-3 months",
        examples: ["Oven idle time reduction", "Scheduling improvements", "Lighting controls"],
        productivityGain: 2.0,
        range: "1.5-2.8%",
        successRate: 89
      },
      mediumTerm: {
        timeframe: "3-12 months", 
        examples: ["Install heat recovery", "Process controls", "HVAC optimization"],
        productivityGain: 1.8,
        range: "1.2-2.4%",
        successRate: 76
      },
      longTerm: {
        timeframe: "12+ months",
        examples: ["Full energy management system", "Advanced monitoring", "Predictive controls"],
        productivityGain: 0.6,
        range: "0.4-1.2%",
        successRate: 68
      },
      cumulativeImpact: {
        totalGain: 4.4,
        range: "3.1-6.4%"
      }
    },
    
    productivityFactors: [
      {
        name: "Equipment Efficiency",
        impact: 12,
        confidence: "8-16%",
        confidenceLevel: 95,
        description: "Optimised equipment reduces waste, increases output"
      },
      {
        name: "Process Timing", 
        impact: 8,
        confidence: "5-11%",
        confidenceLevel: 95,
        description: "Scheduling + idle time reduction"
      },
      {
        name: "Quality Control",
        impact: 6,
        confidence: "3-9%", 
        confidenceLevel: 95,
        description: "Reduced rework + waste improves productivity ratio"
      }
    ],
    
    priorities: [
      {
        id: 1,
        measure: "Oven Idle Time Reduction",
        savings: 42800,
        payback: 0.9,
        category: "Process Heating",
        confidence: "High",
        description: "Advanced oven scheduling system with predictive controls to minimise idle time while maintaining production flexibility. Includes automated warm-up sequencing based on production schedules.",
        details: "Smart scheduling algorithms reduce average idle time by 65%. Integration with existing production management systems. Real-time monitoring and adjustment capabilities.",
        technical_details: "Smart scheduling algorithms reduce idle time by 65%. Maintains product quality through controlled temperature profiles. Integration with existing production management systems. Real-time monitoring and adjustment capabilities",
        implementation: "Install smart scheduling software, train operators, configure production interfaces",
        productivity: "+2.1% output increase through optimized heating cycles",
        cost_range: "35k-40k",
        partner_company: "HeatControl Systems"
      },
      {
        id: 2,
        measure: "Heat Recovery from Oven Exhaust",
        savings: 21300,
        payback: 1.4,
        category: "Heat Recovery",
        confidence: "High",
        description: "Capture waste heat from oven exhaust systems and redirect to pre-heat incoming air or other thermal processes, reducing overall energy consumption.",
        details: "Heat exchanger efficiency rating >85%. Automated bypass controls for maintenance. Integrated with existing HVAC systems. Continuous temperature and flow monitoring.",
        technical_details: "Heat exchanger efficiency rating >85%. Automated bypass controls for maintenance. Integrated with existing HVAC systems. Continuous temperature and flow monitoring",
        implementation: "Install heat exchangers, ducting modifications, control system integration",
        productivity: "+1.8% efficiency through optimized thermal management",
        cost_range: "25k-30k",
        partner_company: "ThermoRecov Solutions"
      },
      {
        id: 3,
        measure: "Refrigeration Setpoint Optimisation",
        savings: 18900,
        payback: 0.7,
        category: "HVAC Systems",
        confidence: "High",
        description: "Intelligent control system that optimizes refrigeration setpoints based on product requirements, ambient conditions, and usage patterns.",
        details: "Dynamic setpoint adjustment reduces energy use by 15-25%. Product safety monitoring with real-time alerts. Integration with existing refrigeration controls. Predictive maintenance capabilities.",
        technical_details: "Dynamic setpoint adjustment reduces energy use by 15-25%. Product safety monitoring with real-time alerts. Integration with existing refrigeration controls. Predictive maintenance capabilities",
        implementation: "Smart controller installation, sensor network, software configuration",
        productivity: "+0.9% through improved temperature stability",
        cost_range: "8k-10k",
        partner_company: "CoolOpt Partners"
      },
      {
        id: 4,
        measure: "LED Lighting Upgrade",
        savings: 16500,
        payback: 1.2,
        category: "Lighting",
        confidence: "High", 
        description: "Complete retrofit of existing lighting systems with high-efficiency LED fixtures and intelligent lighting controls.",
        details: "70% energy reduction compared to fluorescent. Daylight harvesting and occupancy sensors. 25-year warranty on LED fixtures. Instant-on capability and dimming controls.",
        technical_details: "70% energy reduction compared to fluorescent. Daylight harvesting and occupancy sensors. 25-year warranty on LED fixtures. Instant-on capability and dimming controls",
        implementation: "Fixture replacement, control system installation, commissioning",
        productivity: "+0.4% through improved visibility and reduced maintenance",
        cost_range: "15k-18k",
        partner_company: "LED Solutions Australia"
      },
      {
        id: 5,
        measure: "Compressed Air Leak Management",
        savings: 14100,
        payback: 1.0,
        category: "Compressed Air",
        confidence: "Medium",
        description: "Systematic leak detection and repair program with ultrasonic monitoring equipment and preventive maintenance protocols.",
        details: "Continuous monitoring of air pressure and flow. Automated leak detection with location mapping. Preventive maintenance scheduling. Energy usage reporting and analytics.",
        technical_details: "Continuous monitoring of air pressure and flow. Automated leak detection with location mapping. Preventive maintenance scheduling. Energy usage reporting and analytics",
        implementation: "Leak detection equipment, repair protocols, monitoring system",
        productivity: "+0.6% through optimized pneumatic operations",
        cost_range: "10k-12k",
        partner_company: "AirTight Services"
      }
    ],
    
    fullOpportunities: [
      { measure: "Motor Efficiency Improvements", savings: 12800, payback: 1.8, cost: "15k-20k", partner: "MotorTech Solutions", logo: "🔧" },
      { measure: "Building Insulation Upgrade", savings: 8900, payback: 2.1, cost: "25k-30k", partner: "ThermoShield Pro", logo: "🏠" },
      { measure: "Smart Controls Installation", savings: 7400, payback: 1.5, cost: "10k-15k", partner: "AutoControl Systems", logo: "🎛️" },
      { measure: "Steam System Optimization", savings: 5600, payback: 1.3, cost: "8k-12k", partner: "SteamWise Corp", logo: "💨" },
      { measure: "Power Factor Correction", savings: 3200, payback: 0.8, cost: "5k-8k", partner: "PowerOptim Ltd", logo: "⚡" }
    ],

    isRealAnalysis: false,
    methodology: "Sample demonstration data",
    engineVersion: "demo-1.0.0"
  };

  const displayData = getDisplayData();

  // Thin wrapper components that connect container state to presentational sections
  const CoverPageView = () => <CoverPageSection displayData={displayData} />;

  const ExecutiveSummaryView = () => (
    <ExecutiveSummarySection
      displayData={displayData}
      sampleDataFallback={sampleDataFallback}
    />
  );

  const PriorityOpportunitiesView = () => (
    <PriorityOpportunitiesSection
      displayData={displayData}
      sampleDataFallback={sampleDataFallback}
    />
  );

  const FullOpportunitiesListView = () => (
    <FullOpportunitiesListSection sampleDataFallback={sampleDataFallback} />
  );

  const ProductivityRoadmapView = () => (
    <ProductivityRoadmapSection sampleDataFallback={sampleDataFallback} />
  );

  const BenchmarkComparisonView = () => (
    <BenchmarkComparisonSection
      displayData={displayData}
      sampleDataFallback={sampleDataFallback}
    />
  );

  const ProductivityFactorsView = () => (
    <ProductivityFactorsSection sampleDataFallback={sampleDataFallback} />
  );

  const AssumptionsLimitationsView = () => (
    <AssumptionsLimitationsSection />
  );

  const MethodologyDataSourcesView = () => (
    <MethodologyDataSourcesSection />
  );

  const NextStepsView = () => <NextStepsSection />;



  // Navigation Tabs
  const tabs = [
    { id: 'cover', label: 'Cover', icon: '📋' },
    { id: 'summary', label: 'Executive Summary', icon: '💡' },
    { id: 'priorities', label: 'Priority Opportunities', icon: '🎯' },
    { id: 'full-list', label: 'Full List', icon: '📄' },
    { id: 'roadmap', label: 'Roadmap', icon: '🗺️', premium: true },
    { id: 'benchmark', label: 'Benchmark', icon: '📊', premium: true },
    { id: 'productivity', label: 'Productivity Factors', icon: '🔑', premium: true },
    { id: 'assumptions', label: 'Assumptions', icon: '⚠️', premium: true },
    { id: 'methodology', label: 'Methodology', icon: '🔬', premium: true },
    { id: 'next-steps', label: 'Next Steps', icon: '🚀' }
  ];

  const renderActiveSection = () => {
    switch (activeTab) {
      case 'cover':
        return <CoverPageView />;
      case 'summary':
        return <ExecutiveSummaryView />;
      case 'priorities':
        return <PriorityOpportunitiesView />;
      case 'full-list':
        return <FullOpportunitiesListView />;
      case 'roadmap':
        return <ProductivityRoadmapView />;
      case 'benchmark':
        return <BenchmarkComparisonView />;
      case 'productivity':
        return <ProductivityFactorsView />;
      case 'assumptions':
        return <AssumptionsLimitationsView />;
      case 'methodology':
        return <MethodologyDataSourcesView />;
      case 'next-steps':
        return <NextStepsView />;
      default:
        return <CoverPageView />;
    }
  };

  // Show loading state with skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header Skeleton */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg animate-pulse" />
                <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-32 animate-pulse" />
              </div>
              <div className="flex items-center space-x-4">
                <div className="h-10 bg-gray-200 dark:bg-gray-600 rounded-lg w-48 animate-pulse" />
                <div className="h-10 bg-gray-200 dark:bg-gray-600 rounded w-24 animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs Skeleton */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8 overflow-x-auto">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="py-4 px-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            {/* Title Skeleton */}
            <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-64 animate-pulse" />
            
            {/* Stats Cards Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24 mb-3 animate-pulse" />
                  <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-32 animate-pulse" />
                </div>
              ))}
            </div>

            {/* Content Blocks Skeleton */}
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
                <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-48 mb-4 animate-pulse" />
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-full animate-pulse" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-5/6 animate-pulse" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-4/6 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-emerald-600 dark:bg-emerald-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">RA</span>
              </div>
              <span className="text-xl font-bold text-slate-800 dark:text-white">Rely Ability</span>
            </Link>
            
            <div className="flex items-center space-x-4">
              <div className="bg-emerald-600 dark:bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium">
                ✓ Full Professional Analysis
              </div>
              <Link 
                to="/"
                className="text-slate-600 dark:text-gray-300 hover:text-slate-800 dark:hover:text-white font-medium transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-500 dark:border-emerald-400 text-emerald-600 dark:text-emerald-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.premium && (
                  <span className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-1 rounded text-xs">Premium</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderActiveSection()}
      </div>
    </div>
  );
};

export default SampleESAReport;
